import { DISCOUNT_IDS } from '@constants/apiConstants';
import { CURRENCY_CODE } from '@constants/app';
import {
  ChargeAmount,
  Discount,
  ProductSubtype,
  ProductType,
  QuoteStatus,
  QuoteUnavailabilityReasonType,
  Status,
} from '@generated/gql/graphql';
import {
  BoilerInsuranceQuote,
  IncomeProtectorInsuranceQuote,
} from '@src/types/insurance';
import {
  calculateDiscountedPrice,
  getMonthlyCost,
  parseCurrency,
  sumPrices,
} from '@utils/price';

interface Basket {
  quotes: Quote[];
}

interface Quote {
  product: {
    type: string;
    subtype: string;
  };
  selected: boolean;
  totalMonthlyCost: ChargeAmount;
  status: QuoteStatus;
}

const isQuoteValid = (quote: Quote): boolean => {
  if (!quote) return false;
  const { status, reasons } = quote.status;

  const isAvailable = status === Status.Available;
  const isIncomplete =
    reasons?.every(
      (r) => r.type === QuoteUnavailabilityReasonType.Ineligible
    ) ?? false;

  return isAvailable || isIncomplete;
};

type InsuranceQuoteForSubtype<S extends ProductSubtype> =
  S extends ProductSubtype.BoilerHomeCover
    ? BoilerInsuranceQuote
    : S extends ProductSubtype.IncomeProtector
      ? IncomeProtectorInsuranceQuote
      : never;
const getInsuranceQuote = <S extends ProductSubtype>(
  quotes: Quote[],
  subtype: S
): {
  quote: InsuranceQuoteForSubtype<S> | null;
  available: boolean;
  selected: boolean;
  monthlyCost: ChargeAmount;
  discount: Discount | null;
} => {
  const quote = quotes.find(
    (quote) =>
      quote.product.type === ProductType.Insurance &&
      quote.product.subtype === subtype &&
      !quote.status.reasons?.some(
        (reason) =>
          reason.type === QuoteUnavailabilityReasonType.IneligiblePermanent
      )
  ) as InsuranceQuoteForSubtype<S>;

  const selected = !!quote?.selected;
  const monthlyCost = selected ? parseCurrency(quote.totalMonthlyCost) : null;

  const bhcDiscount: Discount | null =
    quote?.discounts?.find(
      (d) =>
        d.qualified &&
        d.id === DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT
    ) ?? null;

  return {
    quote: quote ?? null,
    available: isQuoteValid(quote),
    selected,
    monthlyCost: getMonthlyCost(monthlyCost),
    discount: bhcDiscount,
  };
};

const formatInsurance = (basket: Basket) => {
  const quotes = basket.quotes as Quote[];

  const boilerInsurance = getInsuranceQuote(
    quotes,
    ProductSubtype.BoilerHomeCover
  );
  const incomeProtector = getInsuranceQuote(
    quotes,
    ProductSubtype.IncomeProtector
  );

  const selectedInsuranceQuotes = [boilerInsurance, incomeProtector]
    .map((insurance) => insurance.quote)
    .filter(
      (quote): quote is BoilerInsuranceQuote | IncomeProtectorInsuranceQuote =>
        !!quote?.selected
    );
  const selected = selectedInsuranceQuotes.length > 0;

  const monthlyCost = selectedInsuranceQuotes.reduce(
    (total, { totalMonthlyCost }) => sumPrices(total, totalMonthlyCost),
    {
      currency: CURRENCY_CODE,
      value: '0',
    }
  );

  const bhcDiscount = boilerInsurance.discount;
  let discount: { price: ChargeAmount; durationMonths: number } | null = null;

  if (bhcDiscount && boilerInsurance.selected && boilerInsurance.quote) {
    const discountedBhcCost = calculateDiscountedPrice(
      boilerInsurance.quote.totalMonthlyCost,
      bhcDiscount
    );
    const ipCost =
      incomeProtector.selected && incomeProtector.quote
        ? incomeProtector.quote.totalMonthlyCost
        : { currency: CURRENCY_CODE, value: '0' };
    discount = {
      price: sumPrices(discountedBhcCost, ipCost),
      durationMonths: bhcDiscount.creditFrequency ?? 0,
    };
  }

  const available = boilerInsurance.available || incomeProtector.available;
  return {
    boilerInsurance,
    incomeProtector,
    selected,
    monthlyCost,
    available,
    discount,
  };
};

export { formatInsurance };
